import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Button, Spin, Row, Col, message } from 'antd';
import debounce from 'lodash/debounce';
import { showError, showSuccess } from '@components/notification';
import { usePermission } from '@contexts/Auth/UsePermission';
import {
  apiCreateFavoriteFilter,
  apiUpdateFavoriteFilter
} from '@services/ManageCustomers/FavoriteFilterServices';
import { apiGetAllCustomer } from '@services/ManageCustomers/CustomerServices';
import { apiGetDevicesInformationV2 } from '@services/ManageDevices/DeviceServices';

export default function FavoriteFilterForm({ visible, onClose, onSuccess, editingFilter = null }) {
  const [form] = Form.useForm();
  const { favoriteFilter } = usePermission();
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('customer');
  const [customers, setCustomers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);

  // Fetch customers
  const fetchCustomers = async (searchText = '') => {
    setLoadingCustomers(true);
    try {
      const response = await apiGetAllCustomer({
        keyWord: searchText || undefined,
        page: 1,
        size: 100
      });
      const customerData = response.data?.data || [];
      setCustomers(
        customerData.map((item) => ({
          label: `${item.code} - ${item.name}`,
          value: item.code
        }))
      );
    } catch (error) {
      console.error('Failed to fetch customers', error);
      message.error('Lỗi khi lấy danh sách khách hàng');
    }
    setLoadingCustomers(false);
  };

  // Fetch devices
  const fetchDevices = async (searchText = '') => {
    setLoadingDevices(true);
    try {
      const response = await apiGetDevicesInformationV2({
        keyWord: searchText || undefined,
        page: 1,
        size: 100
      });
      const deviceData = response?.data?.data || [];
      setDevices(
        deviceData.map((item) => ({
          label: `${item.serial_no} - IMEI: ${item.imei}`,
          value: item.serial_no
        }))
      );
    } catch (error) {
      console.error('Failed to fetch devices', error);
      message.error('Lỗi khi lấy danh sách thiết bị');
    }
    setLoadingDevices(false);
  };

  const handleSearchCustomers = debounce((value) => {
    fetchCustomers(value);
  }, 300);

  const handleSearchDevices = debounce((value) => {
    fetchDevices(value);
  }, 300);

  useEffect(() => {
    if (visible) {
      // Fetch initial data
      fetchCustomers();
      fetchDevices();

      if (editingFilter) {
        form.setFieldsValue({
          filter_name: editingFilter.filter_name,
          filter_description: editingFilter.filter_description,
          filter_type: editingFilter.filter_type,
          item_ids: editingFilter.item_ids || []
        });
        setFilterType(editingFilter.filter_type);
      } else {
        form.resetFields();
        setFilterType('customer');
      }
    }
  }, [visible, editingFilter, form]);

  const handleSubmit = async (values) => {
    try {
      if (!values.filter_name || values.filter_name.trim() === '') {
        showError('Tên bộ lọc không được để trống');
        return;
      }

      if (!values.item_ids || values.item_ids.length === 0) {
        showError('Vui lòng chọn ít nhất một mục');
        return;
      }

      setLoading(true);

      const submitData = {
        filter_name: values.filter_name,
        filter_description: values.filter_description || '',
        filter_type: values.filter_type,
        item_ids: values.item_ids
      };

      let res;
      if (editingFilter) {
        res = await apiUpdateFavoriteFilter(editingFilter.id, submitData);
        showSuccess({ message: 'Cập nhật bộ lọc thành công' });
      } else {
        res = await apiCreateFavoriteFilter(submitData);
        showSuccess({ message: 'Tạo bộ lọc thành công' });
      }

      if (res.data?.success) {
        form.resetFields();
        onSuccess();
        onClose();
      } else {
        showError(res.data?.message || 'Lỗi khi lưu bộ lọc');
      }
    } catch (err) {
      console.error('Error submitting filter:', err);
      showError('Lỗi khi lưu bộ lọc');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={editingFilter ? 'Chỉnh sửa bộ lọc' : 'Tạo bộ lọc mới'}
      open={visible}
      onCancel={onClose}
      maskClosable={false}
      width={600}
      footer={[
        <Button key="back" onClick={onClose}>
          Hủy
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={() => form.submit()}>
          {editingFilter ? 'Cập nhật' : 'Tạo bộ lọc'}
        </Button>
      ]}>
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
          {/* Filter Name */}
          <Form.Item
            label="Tên bộ lọc"
            name="filter_name"
            rules={[{ required: true, message: 'Vui lòng nhập tên bộ lọc' }]}>
            <Input placeholder="Ví dụ: Khách hàng nhóm A" />
          </Form.Item>

          {/* Filter Description */}
          <Form.Item label="Mô tả" name="filter_description">
            <Input.TextArea placeholder="Mô tả bộ lọc này để dễ nhớ" rows={2} />
          </Form.Item>

          {/* Filter Type */}
          <Form.Item
            label="Loại lọc"
            name="filter_type"
            rules={[{ required: true, message: 'Vui lòng chọn loại lọc' }]}>
            <Select
              placeholder="Chọn loại lọc"
              options={[
                { label: 'Khách hàng', value: 'customer' },
                {
                  label: 'Thiết bị',
                  value: 'device',
                  disabled: !favoriteFilter.createDevice
                }
              ]}
              onChange={(value) => {
                setFilterType(value);
                form.setFieldValue('item_ids', []);
              }}
            />
          </Form.Item>

          {/* Item IDs - Customers or Devices */}
          <Form.Item
            label={filterType === 'customer' ? 'Chọn khách hàng' : 'Chọn thiết bị'}
            name="item_ids"
            rules={[
              {
                required: true,
                message: `Vui lòng chọn ít nhất một ${
                  filterType === 'customer' ? 'khách hàng' : 'thiết bị'
                }`
              }
            ]}>
            <Select
              mode="multiple"
              placeholder={
                filterType === 'customer' ? 'Tìm kiếm khách hàng...' : 'Tìm kiếm thiết bị...'
              }
              options={filterType === 'customer' ? customers : devices}
              loading={filterType === 'customer' ? loadingCustomers : loadingDevices}
              onSearch={filterType === 'customer' ? handleSearchCustomers : handleSearchDevices}
              filterOption={false}
              optionLabelProp="label"
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
}
